import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Brain, Plus, Power, Drop, Play, Heartbeat, Eye, Star, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = {
  engine_id: "", name: "", public_ip: "", esl_port: "8084",
  max_concurrent_calls: "10", health_url: "", notes: "",
};

const STATUS_STYLES = {
  ONLINE: "bg-emerald-500/15 text-emerald-600",
  DEGRADED: "bg-amber-500/15 text-amber-600",
  OFFLINE: "bg-zinc-500/15 text-zinc-500",
  DISABLED: "bg-zinc-500/15 text-zinc-500",
  DRAINING: "bg-sky-500/15 text-sky-600",
};

function EngineStatus({ status, engineId }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || "bg-muted text-muted-foreground"}`}
      data-testid={engineId ? `engine-status-${engineId}` : `engine-status-${status}`}>
      {status}
    </span>
  );
}

export default function AIEngines() {
  const { user } = useAuth();
  const canAdd = can(user, "servers-ai-engines", "add");
  const canEdit = can(user, "servers-ai-engines", "edit");
  const canDelete = can(user, "servers-ai-engines", "delete");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [details, setDetails] = useState(null);
  const [confirm, setConfirm] = useState(null); // {action, engine}

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/ai-engines/overview");
      setRows(data.engines || []);
      setTotals(data.totals || null);
    } finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 8000);  // live-refresh active calls
    return () => clearInterval(t);
  }, [load]);

  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEdit(s);
    setForm({
      engine_id: s.engine_id, name: s.name, public_ip: s.public_ip,
      esl_port: String(s.esl_port || 8084), max_concurrent_calls: String(s.max_concurrent_calls || 10),
      health_url: s.health_url || "", notes: s.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const esl = parseInt(form.esl_port);
      const maxc = parseInt(form.max_concurrent_calls);
      const body = {
        engine_id: form.engine_id.trim(), name: form.name.trim(), public_ip: form.public_ip.trim(),
        esl_port: Number.isFinite(esl) ? esl : 8084,
        max_concurrent_calls: Number.isFinite(maxc) && maxc > 0 ? maxc : 10,
        health_url: form.health_url || null, notes: form.notes || null,
      };
      if (edit) {
        const { engine_id, ...patch } = body;
        await api.patch(`/ai-engines/${edit.id}`, patch);
      } else {
        await api.post("/ai-engines", body);
      }
      toast.success(edit ? "Engine updated" : "Engine added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, id) => {
    setBusy(id);
    try { await fn(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const runAction = (action, s) => act(async () => {
    if (action === "enable") await api.post(`/ai-engines/${s.id}/enable`);
    if (action === "disable") await api.post(`/ai-engines/${s.id}/disable`);
    if (action === "drain") await api.post(`/ai-engines/${s.id}/drain`);
    if (action === "resume") await api.post(`/ai-engines/${s.id}/resume`);
    if (action === "remove") await api.delete(`/ai-engines/${s.id}`);
    toast.success(`Engine ${action}d`);
    setConfirm(null);
  }, s.id);

  const healthCheck = (s) => act(async () => {
    const { data } = await api.post(`/ai-engines/${s.id}/health-check`);
    toast.success(`Probe ${data.healthy ? "reachable" : "unreachable"} — status ${data.status}`);
  }, s.id);

  const setDefault = (s) => act(async () => {
    await api.post(`/ai-engines/${s.id}/default`, { is_default: !s.is_default });
    toast.success(s.is_default ? "Default cleared" : "Set as default engine");
  }, s.id);

  const viewDetails = async (s) => {
    try { setDetails((await api.get(`/ai-engines/${s.id}`)).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const needsConfirm = (action, s) => setConfirm({ action, engine: s });

  return (
    <div>
      <PageHeader title="AI Engine Servers"
        subtitle="Remote conversation-engine servers. Calls are auto-assigned by health + spare capacity — never per campaign.">
        {canAdd && (
        <Button onClick={openNew} data-testid="create-engine-btn" className="rounded-lg">
          <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Engine
        </Button>
        )}
      </PageHeader>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="engine-totals">
          {[
            ["Engines", `${totals.online}/${totals.engine_count} online`],
            ["Capacity", `${totals.total_active_calls}/${totals.total_capacity} calls`],
            ["Available", `${totals.total_available_slots} slots`],
            ["Utilization", `${totals.utilization_pct}%`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card card-shadow p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
              <p className="text-lg font-semibold tabular-nums mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="engine-empty" icon={Brain} title="No AI engines"
            description="Register a remote AI Engine server so remote calls can be scheduled to it automatically." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="engine-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Engine ID</TableHead><TableHead>IP</TableHead>
                <TableHead>Status</TableHead><TableHead>Default</TableHead><TableHead className="text-right">Active / Max</TableHead>
                <TableHead className="text-right">Available</TableHead><TableHead className="text-right">Util %</TableHead>
                <TableHead>Last Health</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`engine-row-${s.engine_id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-xs">{s.engine_id}</TableCell>
                    <TableCell className="font-mono text-sm">{s.public_ip}</TableCell>
                    <TableCell><EngineStatus status={s.status} engineId={s.engine_id} /></TableCell>
                    <TableCell>
                      {canEdit ? (
                      <button onClick={() => setDefault(s)} disabled={busy === s.id}
                        data-testid={`engine-default-${s.engine_id}`}
                        className={`inline-flex items-center gap-1 text-xs ${s.is_default ? "text-amber-500" : "text-muted-foreground hover:text-foreground"}`}>
                        <Star className="w-4 h-4" weight={s.is_default ? "fill" : "regular"} /> {s.is_default ? "Default" : "Set"}
                      </button>
                      ) : (
                        s.is_default ? <span className="text-xs text-amber-500">Default</span> : <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`engine-active-${s.engine_id}`}>
                      {s.active_calls} / {s.max_concurrent_calls}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{s.available_slots}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.utilization_pct}%</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.last_health_check ? new Date(s.last_health_check).toLocaleTimeString() : "—"}
                      {s.last_error ? <span className="block text-destructive truncate max-w-[140px]">{s.last_error}</span> : null}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {canEdit && (
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => healthCheck(s)} disabled={busy === s.id} data-testid={`engine-health-${s.engine_id}`} title="Health check"><Heartbeat className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => viewDetails(s)} data-testid={`engine-details-${s.engine_id}`} title="View details"><Eye className="w-4 h-4" /></Button>
                        {canEdit && (s.status === "DRAINING"
                          ? <Button variant="ghost" size="sm" className="rounded-lg px-2 text-sky-600" onClick={() => runAction("resume", s)} disabled={busy === s.id} data-testid={`engine-resume-${s.engine_id}`} title="Resume"><Play className="w-4 h-4" /></Button>
                          : <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => needsConfirm("drain", s)} disabled={busy === s.id || !s.enabled} data-testid={`engine-drain-${s.engine_id}`} title="Drain"><Drop className="w-4 h-4" /></Button>)}
                        {canEdit && (s.enabled
                          ? <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => needsConfirm("disable", s)} disabled={busy === s.id} data-testid={`engine-disable-${s.engine_id}`} title="Disable"><Power className="w-4 h-4" /></Button>
                          : <Button variant="ghost" size="sm" className="rounded-lg px-2 text-emerald-600" onClick={() => runAction("enable", s)} disabled={busy === s.id} data-testid={`engine-enable-${s.engine_id}`} title="Enable"><Power className="w-4 h-4" /></Button>)}
                        {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openEdit(s)} data-testid={`edit-engine-${s.engine_id}`} title="Edit" aria-label="Edit"><PencilSimple className="w-4 h-4" /></Button>
                        )}
                        {canDelete && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => needsConfirm("remove", s)} data-testid={`delete-engine-${s.engine_id}`} title="Remove" aria-label="Remove"><Trash className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      {/* Add / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="engine-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "Add"} AI Engine</DialogTitle>
            <DialogDescription>Remote conversation-engine server. Calls are auto-scheduled — no campaign assignment.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Engine ID</Label><Input data-testid="engine-id" value={form.engine_id} disabled={!!edit} onChange={(e) => setForm({ ...form, engine_id: e.target.value })} placeholder="ai-engine-02" /></div>
              <div className="space-y-2"><Label>Name</Label><Input data-testid="engine-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="AI Engine 02" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2"><Label>Public IP</Label><Input data-testid="engine-ip" value={form.public_ip} onChange={(e) => setForm({ ...form, public_ip: e.target.value })} placeholder="157.90.96.60" /></div>
              <div className="space-y-2"><Label>ESL Port</Label><Input data-testid="engine-esl-port" type="number" value={form.esl_port} onChange={(e) => setForm({ ...form, esl_port: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Max concurrent calls</Label><Input data-testid="engine-max" type="number" value={form.max_concurrent_calls} onChange={(e) => setForm({ ...form, max_concurrent_calls: e.target.value })} /></div>
              <div className="space-y-2"><Label>Health URL <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="engine-health-url" value={form.health_url} onChange={(e) => setForm({ ...form, health_url: e.target.value })} placeholder="https://…/health" /></div>
            </div>
            <div className="space-y-2"><Label>Notes <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="engine-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <p className="text-xs text-muted-foreground">Media URL defaults to <code>wss://&lt;ip&gt;:8090</code>. New engines start OFFLINE and become ONLINE after a successful health check.</p>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving || !form.engine_id.trim() || !form.name.trim() || !form.public_ip.trim()} data-testid="submit-engine-btn" className="rounded-lg">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm destructive/disruptive actions */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="engine-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.action === "remove" ? "Delete AI engine?"
                : confirm?.action === "drain" ? "Drain AI engine?"
                : confirm?.action === "disable" ? "Disable AI engine?"
                : "Confirm?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.action === "drain" && `New calls will stop being assigned to “${confirm?.engine?.name}”. Existing calls continue until they finish.`}
              {confirm?.action === "disable" && `“${confirm?.engine?.name}” will be removed from scheduling immediately.`}
              {confirm?.action === "remove" && `This permanently deletes “${confirm?.engine?.name}” (${confirm?.engine?.public_ip || confirm?.engine?.engine_id}). Only allowed when it has no active calls. Peer UFW rules for this IP will be reconciled. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirm(null)} data-testid="engine-confirm-cancel">Cancel</Button>
            <Button
              className={`rounded-lg ${confirm?.action === "remove" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
              onClick={() => runAction(confirm.action, confirm.engine)}
              data-testid="engine-confirm-ok"
            >
              {confirm?.action === "remove" ? "Delete" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details */}
      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent data-testid="engine-details-dialog">
          <DialogHeader><DialogTitle>{details?.name} · {details?.engine_id}</DialogTitle>
            <DialogDescription>{details?.media_url}</DialogDescription></DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>Status: <EngineStatus status={details?.status} /></div>
              <div>Healthy: {details?.healthy ? "yes" : "no"}</div>
              <div>Active: {details?.active_calls} / {details?.max_concurrent_calls}</div>
              <div>Available: {details?.available_slots}</div>
              <div>Failures: {details?.failure_count}</div>
              <div>Last error: {details?.last_error || "—"}</div>
            </div>
            <div>
              <p className="font-medium mb-1">Active sessions ({details?.active_sessions?.length || 0})</p>
              <div className="max-h-[30vh] overflow-y-auto space-y-1">
                {(details?.active_sessions || []).length === 0 && <p className="text-xs text-muted-foreground">None.</p>}
                {(details?.active_sessions || []).map((s) => (
                  <div key={s.session_id} className="rounded border border-border p-2 font-mono text-xs">{s.session_id} · {s.campaign_prefix || "—"}</div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
